import _ from 'lodash';
import { Suspense, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ActivityContainer } from './components/container/activityContainer';
import InfoDialog from './components/InfoDialog/InfoDialog';
import { FilterContext } from './contexts';
import {
  HIDE_GLOBAL_SEARCH,
  PREVIEW_ROUTES,
  Routing,
  SHOW_DRILLDOWN_MENU_ROUTES,
} from './routes';
import { useLoadRoomTypes } from './hooks/useRoomTypes';
import { SharePopupController } from './common/SharePopupController';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { Loader } from './common';
import CreateTicketModal from './common/CreateTicketModal';
import { PortalInfoIcon } from './common/PortalInfoIcon';
import { SnackbarDownloadFileUI } from './common/DownloadFileController/SnackbarDownloadFileUI';
import { FetchFilterController } from './common/FetchFilterController';
import { cachedApiGetMemberOrganizationPermissions } from './helpers/api';
import { useUserContext } from './contexts/UserContext';
import { useRealNotifications } from './hooks/useRealNotifications';
import { SearchQueryFilterSync } from './common/SearchQueryFilterSync';
import { resetParamsInSequence } from './redux/slicers/filters/filterSlicer';
import { dispatcher } from './helpers/projectHelper';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID, HIDE_SECTION, upgradePopup } from './constants/constants';
import {
  createFrappeFrameAndLogout,
  getOrGenerateEpochUUID,
  sendDataToSubDomain,
  setLocalStorage,
} from './helpers/jsHelper';
import LearnVideoModel from './components/learn/LearnVideoModel';
import GamificationTooltip from './components/gamificationTooltip';
import { useBaseAPICalls } from './hooks/useBaseAPICalls';
import { PaymentFormForImages } from './components/admin/curator/RenderPreviewsDialog/PaymentFormForImages';
import GamifyOnBoarding from './components/gamifyOnBoarding/gamifyOnBoarding';
import { AppFeedback } from './common/AppFeedback';
import {
  ON_BOARDING_ROUTE,
  REQUESTS_ROUTE_PDP_DETAIL,
  SIGNUP_ROUTE,
} from './constants/routePathConstants';
import TagManager from 'react-gtm-module';
import { RequestVideoDialog } from './components/admin/curator/RequestVideoDialog';
import { useSubscriptionSetupAPICalls } from './hooks/useSubscriptionSetupAPICalls';
import { navigateToSelectedSubscriptionPlans } from './screens/subscriptionComponents/subscriptionHelper';
import UploadBulkTextureModal from './components/textureBulkUpload/UploadBulkTextureModal';
import { selectUserActiveSubscriptionPlanPriceId } from './redux/slicers/subscriptionSlicer';
import { selectShareData, sharePopupActions } from './redux/slicers/sharePopupSlicer';
import {
  addToCollectionActions,
  selectAddToCollectionItems,
} from './redux/slicers/addToCollectionPopupSlicer';
import useCopyInfoSync from './hooks/useCopyInfoSync';
import useResumeModelUploads from './hooks/useResumeModelUploads';
import { useDispatch, useSelector } from 'react-redux';
import { isPublicPageView, isRetailAutomation } from './components/Configurator/utils';
import { OrganizationSelectModal } from './components/OrganizationSelectModal/OrganizationSelectModal';
import { ContactSupportForm } from './common/ContactSupportForm/ContactSupportForm';
import {
  RequestTutorialVideo,
  RequestPackWelcomeModal,
} from './screens/Requests/RequestTutorialVideo';
import { GuestUserResetPasswordPopup } from './screens/GuestUserResetPasswordPopup';
import RegisterGuestUserPopup from './screens/RegisterGuestUserPopup';
import useUserPilot from './hooks/useUserPilot';
import { segmentLoad } from './helpers/segmentHelper';
import useCurrentOrganizationInfo from './hooks/useCurrentOrganizationInfo';
import { ManageOrgsChooseModel } from './screens/NewRegisterOnboard/Index';
import { UploadFileProgressModal } from './components/UploadFileProgressModal';
import BackgroundUploadProcessor from './components/BackgroundUploadProcessor';
import { ConfiguratorRequestForm } from './common/ConfiguratorRequestForm/ConfiguratorRequestForm';
import { usePopupBlockerModal } from './hooks/usePopupBlockerModal';
import { useHubspotChatbotPopup } from './hooks/useHubspotChatbotPopup';
import { useCacheClearWithAppVersion } from './hooks/useCacheClearWithAppVersion';
import { EnterpriseForm } from './screens/subscriptionComponents/EnterpriseForm';
import { BookADemoForm } from './common/BookADemoForm/BookADemoForm';
import { ManageAssignEditorModel } from './screens/NewProductUIV2/AssignEditor/Index';
import useFooterVisibility from './hooks/useFooterVisibility';

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID,
};
const ACCEPTED_URLS = [SIGNUP_ROUTE, ON_BOARDING_ROUTE];

export const hideSearchBar = (pathname) =>
  _.some(HIDE_GLOBAL_SEARCH, (route) => {
    return matchPath({ path: route }, pathname) !== null;
  });

export const assessDrillDownSideMenuRoute = (pathname) =>
  _.some(SHOW_DRILLDOWN_MENU_ROUTES, (rte) => {
    return matchPath({ path: rte }, pathname) !== null;
  });

export const assessPreviewRoute = (pathname) =>
  _.some(PREVIEW_ROUTES, (rte) => {
    return matchPath({ path: rte }, pathname) !== null;
  });

export default function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const pathname = location?.pathname;
  const shareData = useSelector(selectShareData);
  const selectedList = useSelector(selectAddToCollectionItems);
  const { currentUser, isGuestLogin, updateUserData, isFreemiumUser } = useUserContext();
  const { isFreemiumOrSelfServeUser } = useCurrentOrganizationInfo();


  // TODO: refactor filter context
  const [showDrilldownMenu, setShowDrilldownMenu] = useState(false);
  const [hideSidemenu, setHideSidemenu] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState(null);
  const isActivePlanPriceId = useSelector(selectUserActiveSubscriptionPlanPriceId);
  const isPdpDetailRoute = location.pathname.includes(REQUESTS_ROUTE_PDP_DETAIL.replace(':id', ''));

  useFooterVisibility({
    footerClass: 'hide-hub-spot-icon',
    targetSelector: '#hubspot-messages-iframe-container',
  });

  useCacheClearWithAppVersion();

  // Sync the "Paste & Apply" copyInfo state across already-open tabs.
  useCopyInfoSync();

  // Resume any interrupted 3D-model uploads (on app load and when the network is regained).
  useResumeModelUploads(currentUser);

  // hotjar script data add user details.
  useEffect(() => {
    // here we checking the hotsjar script loaded proper or not.
    if (typeof window.hj === 'function') {
      localStorage.setItem('hjDebug', true);
      const user = currentUser?.user || { id: getOrGenerateEpochUUID() };
      window.hj('identify', user?.id, {
        email: user?.email,
        name: user?.name,
        user_id: user?.id,
      });
    }
  }, [currentUser]);

  useEffect(() => {
    // This line stop the page scroll when we refresh the browser tab.
    window.history.scrollRestoration = 'manual';
  }, []);

  // Store UTM params in sessionStorage on initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    utmKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        sessionStorage.setItem(key, value);
      }
    });
  }, []);

  useEffect(() => {
    // initialize the GTM
    if (!isPublicPageView()) {
      TagManager.initialize(tagManagerArgs);

      // Clear Frappe logout flag on app load if logout flag is set for frappe
      createFrappeFrameAndLogout();
    }
  }, []);

  useEffect(() => {
    if (
      !isGuestLogin &&
      isFreemiumOrSelfServeUser !== undefined &&
      isFreemiumOrSelfServeUser !== 'undefined'
    ) {
      const param = new URLSearchParams(location.search);
      if (param.get('selectedPlanId')) {
        navigateToSelectedSubscriptionPlans(
          {
            plan: param.get('selectedPlanId'),
            price: param.get('selectedPlanPriceId'),
            interval: param.get('selectedInterval'),
            isTextureType: param.get('isTextureType'),
          },
          isFreemiumOrSelfServeUser
        );
      }
    }
    const websiteFrames = document.querySelectorAll('.website-iframe');
    if (websiteFrames.length > 0) {
      for (let i = 0; i < websiteFrames.length; i++) {
        websiteFrames[i].addEventListener('load', websiteFrameListener);
      }
    }
    return () => {
      if (websiteFrames.length > 0) {
        for (let i = 0; i < websiteFrames.length; i++) {
          websiteFrames[i].removeEventListener('load', websiteFrameListener);
        }
      }
    };
  }, [isGuestLogin, isFreemiumOrSelfServeUser]);

  useEffect(() => {
    setShowDrilldownMenu(assessDrillDownSideMenuRoute(pathname));
    setHideSidemenu(assessPreviewRoute(pathname));
    if (currentUser) {
      segmentLoad();
      if (!isRetailAutomation()) {
        updateUserData();
      }
    }
    sendDataToSubDomain(pathname, currentUser);

    const timerId = setTimeout(() => {
      sendDataToSubDomain(pathname, currentUser);
    }, 2000);

    return () => {
      if (timerId) clearInterval(timerId);
      dispatcher(resetParamsInSequence());
    };
  }, [pathname, currentUser?.organization, isGuestLogin, isActivePlanPriceId]);

  const websiteFrameListener = () => {
    sendDataToSubDomain(pathname, currentUser);
  };

  const filterContext = {
    hideSidemenu: hideSidemenu,
    showDrilldownMenu: showDrilldownMenu,
    breadcrumbs: breadcrumbs,
    setBreadcrumbs: setBreadcrumbs,
  };

  // loads room type options so they are available in the app
  useLoadRoomTypes();

  // get real time notifications
  useRealNotifications();

  // set base data
  useBaseAPICalls(); // https://app.clickup.com/t/3326989/DEV-36576 (frontend homepage optimization)

  // set subscription data
  useSubscriptionSetupAPICalls();

  // initializing the userpilot onboarding
  useUserPilot();

  usePopupBlockerModal();

  useHubspotChatbotPopup();

  const addTransparentToBody = () => {
    const isPublicPage = HIDE_SECTION.some((url) =>
      location?.pathname?.match(url)?.length ? true : false
    );
    if (isPublicPage) document.body.classList.add('bg-transparent');
    else document.body.classList.remove('bg-transparent');
  };

  useEffect(() => {
    if (ACCEPTED_URLS.includes(location.pathname)) {
      window.dataLayer.push({
        event: 'pageview',
      });
    }
    // Below condtion added for close the share and add to collection modal when the user change the route from browser back button.
    if (selectedList && selectedList?.length > 0) {
      dispatch(addToCollectionActions.clear());
    }
    if (shareData && Object.keys(shareData)?.length > 0) {
      dispatch(sharePopupActions.clear());
    }

    window.addEventListener('load', addTransparentToBody);
    return () => window.removeEventListener('load', addTransparentToBody);
  }, [location.pathname]);

  return (
    <ActivityContainer>
      <Suspense fallback={<Loader loading position="fixed" />}>
        <ErrorBoundary>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <FetchFilterController />
            <FilterContext.Provider value={filterContext}>
              <Routing />
            </FilterContext.Provider>
            <SharePopupController />
            {/* <CreateTicketModal />  // commenting as currently we are not using this */}
            <ToastContainer
              position="bottom-center"
              autoClose={5000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss={false}
              draggable
              pauseOnHover
              limit={5}
            />
            <PortalInfoIcon />
            <ManageOrgsChooseModel />
            <ManageAssignEditorModel />
            <InfoDialog />
            <SnackbarDownloadFileUI />
            <SearchQueryFilterSync />
            <ContactSupportForm />
            <ConfiguratorRequestForm />
            <LearnVideoModel />
            <GamifyOnBoarding />
            <PaymentFormForImages />
            <AppFeedback />
            <GamificationTooltip />
            <RequestVideoDialog />
            <RequestTutorialVideo />
            <RequestPackWelcomeModal />
            <UploadBulkTextureModal />
            <UploadFileProgressModal currentUser={currentUser} />
            <BackgroundUploadProcessor />
            <OrganizationSelectModal />
            <RegisterGuestUserPopup />
            <GuestUserResetPasswordPopup />
            <EnterpriseForm />
            <BookADemoForm />
          </GoogleOAuthProvider>
        </ErrorBoundary>
      </Suspense>
    </ActivityContainer>
  );
}